import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import axios from "axios";
import { ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auto-fit map bounds to show all markers
function FitBounds({ sites }) {
  const map = useMap();
  useEffect(() => {
    if (sites.length > 0) {
      const bounds = sites.map((s) => [s.lat, s.lng]);
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 13 });
    }
  }, [sites, map]);
  return null;
}

const STATUS_COLORS = {
  available: "#4EA72E",
  leased: "#d4782f",
  pending: "#0F9ED5",
};

const STATUS_LABELS = {
  available: "Available",
  leased: "Leased",
  pending: "Pending",
};

export default function SiteMap() {
  const [sites, setSites] = useState([]);
  const [stats, setStats] = useState({ total: 0, available: 0, leased: 0, pending: 0 });
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [sitesRes, statsRes] = await Promise.all([
          axios.get(`${API}/sites`),
          axios.get(`${API}/sites/stats`),
        ]);
        setSites(sitesRes.data);
        setStats(statsRes.data);
      } catch (err) {
        console.error("Failed to fetch sites:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredSites = filter === "all" ? sites : sites.filter((s) => s.status === filter);

  const handleRequestSite = (siteName) => {
    navigate(`/contact?site=${encodeURIComponent(siteName)}&role=operator`);
  };

  return (
    <div data-testid="site-map-section">
      {/* Header */}
      <div className="mb-10">
        <p className="text-xs uppercase tracking-[0.25em] font-semibold mb-4" style={{ color: "#d4782f" }}>
          Target Markets
        </p>
        <h2 className="text-3xl md:text-4xl font-bold tracking-tight leading-[1.2] mb-4" style={{ color: "#0E2841" }}>
          Metro Atlanta PadSite Locations
        </h2>
        <p className="text-base leading-relaxed max-w-2xl" style={{ color: "#3A3A3A" }}>
          Explore our target markets across the metro Atlanta area. Available sites are open for
          operator placement. Leased sites are actively generating income.
        </p>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          { label: "Total Sites", value: stats.total, color: "#0E2841" },
          { label: "Available", value: stats.available, color: STATUS_COLORS.available },
          { label: "Leased", value: stats.leased, color: STATUS_COLORS.leased },
          { label: "Pending", value: stats.pending, color: STATUS_COLORS.pending },
        ].map((stat) => (
          <div
            key={stat.label}
            data-testid={`stat-${stat.label.toLowerCase().replace(" ", "-")}`}
            className="p-5 border"
            style={{ borderColor: "rgba(212, 120, 47, 0.2)", backgroundColor: "#FFFFFF" }}
          >
            <span className="text-2xl md:text-3xl font-bold block mb-1" style={{ color: stat.color }}>
              {stat.value}
            </span>
            <span className="text-xs uppercase tracking-[0.15em] font-semibold" style={{ color: "#3A3A3A" }}>
              {stat.label}
            </span>
          </div>
        ))}
      </div>

      {/* Filter Buttons */}
      <div className="flex flex-wrap gap-3 mb-6">
        {[
          { key: "all", label: "All Sites" },
          { key: "available", label: "Available" },
          { key: "leased", label: "Leased" },
          { key: "pending", label: "Pending" },
        ].map((f) => (
          <button
            key={f.key}
            data-testid={`filter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className="px-5 py-3 text-xs uppercase tracking-[0.15em] font-semibold transition-colors rounded-none"
            style={{
              backgroundColor: filter === f.key ? "#d4782f" : "transparent",
              color: filter === f.key ? "#FFFFFF" : "#3A3A3A",
              border: `1px solid ${filter === f.key ? "#d4782f" : "rgba(58,58,58,0.3)"}`,
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Map */}
      {loading ? (
        <div className="h-[500px] flex items-center justify-center" style={{ backgroundColor: "#E8E8E8" }}>
          <p style={{ color: "#3A3A3A" }}>Loading map...</p>
        </div>
      ) : (
        <div data-testid="map-container" className="relative border" style={{ borderColor: "rgba(212, 120, 47, 0.2)" }}>
          <MapContainer
            center={[33.82, -84.35]}
            zoom={10}
            style={{ height: "540px", width: "100%" }}
            scrollWheelZoom={true}
          >
            <TileLayer
              attribution='Tiles &copy; <a href="https://www.esri.com/">Esri</a> &mdash; Source: Esri, TomTom, Garmin, FAO, NOAA, USGS, &copy; OpenStreetMap contributors'
              url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}"
            />
            <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}" />
            <FitBounds sites={filteredSites} />
            {filteredSites.map((site) => (
              <CircleMarker
                key={site.id}
                center={[site.lat, site.lng]}
                radius={10}
                pathOptions={{
                  fillColor: STATUS_COLORS[site.status] || "#FFFFFF",
                  fillOpacity: 0.9,
                  color: "#1A1D23",
                  weight: 2,
                }}
              >
                <Popup>
                  <div style={{ fontFamily: "Arial, sans-serif", minWidth: "220px" }}>
                    <p style={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.1em", color: STATUS_COLORS[site.status], fontWeight: 700, marginBottom: "4px" }}>
                      {STATUS_LABELS[site.status]}
                    </p>
                    <p style={{ fontSize: "15px", fontWeight: 700, color: "#0E2841", marginBottom: "4px" }}>
                      {site.name}
                    </p>
                    <p style={{ fontSize: "12px", color: "#3A3A3A", marginBottom: "2px" }}>{site.address}</p>
                    {site.zone && <p style={{ fontSize: "11px", color: "#3A3A3A", marginBottom: "4px" }}>Zone: {site.zone}</p>}
                    {site.notes && <p style={{ fontSize: "12px", color: "#3A3A3A", marginBottom: "8px" }}>{site.notes}</p>}
                    {site.status === "available" && (
                      <button
                        onClick={() => handleRequestSite(site.name)}
                        style={{
                          backgroundColor: "#d4782f",
                          color: "#1A1D23",
                          border: "none",
                          padding: "6px 14px",
                          fontSize: "11px",
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          cursor: "pointer",
                        }}
                      >
                        Request This Site
                      </button>
                    )}
                  </div>
                </Popup>
              </CircleMarker>
            ))}
          </MapContainer>

          {/* Legend */}
          <div
            className="absolute bottom-4 right-4 z-[1000] p-4"
            style={{ backgroundColor: "rgba(45, 45, 45, 0.92)", backdropFilter: "blur(8px)" }}
            data-testid="map-legend"
          >
            <p className="text-xs uppercase tracking-[0.15em] font-semibold mb-3" style={{ color: "#1A1D23" }}>Legend</p>
            {Object.entries(STATUS_COLORS).map(([key, color]) => (
              <div key={key} className="flex items-center gap-2 mb-1.5">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
                <span className="text-xs" style={{ color: "rgba(255,255,255,0.8)" }}>{STATUS_LABELS[key]}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Site Cards */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredSites.map((site) => (
          <div
            key={site.id}
            data-testid={`site-card-${site.id}`}
            className="p-5 border transition-all"
            style={{ borderColor: "rgba(212, 120, 47, 0.15)", backgroundColor: "#FFFFFF" }}
          >
            <div className="flex items-center justify-between mb-3">
              <span
                className="text-xs uppercase tracking-[0.1em] font-semibold px-2 py-1"
                style={{
                  backgroundColor: STATUS_COLORS[site.status] + "18",
                  color: STATUS_COLORS[site.status],
                }}
              >
                {STATUS_LABELS[site.status]}
              </span>
              <span className="text-xs" style={{ color: "#3A3A3A" }}>{site.zone}</span>
            </div>
            <h4 className="text-sm font-bold mb-1" style={{ color: "#0E2841" }}>{site.name}</h4>
            <p className="text-xs mb-2" style={{ color: "#3A3A3A" }}>{site.address}</p>
            {site.notes && <p className="text-xs mb-3" style={{ color: "rgba(58,58,58,0.7)" }}>{site.notes}</p>}
            {site.status === "available" && (
              <button
                data-testid={`request-site-${site.id}`}
                onClick={() => handleRequestSite(site.name)}
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.1em] font-bold transition-colors mt-1"
                style={{ color: "#d4782f" }}
              >
                Request This Site
                <ArrowRight size={14} />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
